import type { PermissionKey } from "@/constants/permissions"
import { PERMISSIONS } from "@/constants/permissions"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { FeatureFlagKey, FeatureFlagMap } from "@/types/featureFlag"
import {
  Boxes,
  Building2,
  Database,
  Factory,
  FileCheck2,
  FileText,
  FlaskConical,
  Inbox,
  Layers,
  LayoutDashboard,
  type LucideIcon,
  Package,
  Send,
  Users,
} from "lucide-react"

export const MC_BASE = "/material-compliance"

export interface StudioTab {
  labelKey: TranslationKeys
  icon: LucideIcon
  to: string
  flag: FeatureFlagKey
  /** VIEW permission gate. Omit only for tabs anyone within the studio can see. */
  requiresPermission?: PermissionKey
  // Exact-match so a parent tab isn't highlighted while a sibling sub-route is active.
  end?: boolean
}

export interface Studio {
  // Path segment used to detect which studio is active.
  id: string
  labelKey: TranslationKeys
  icon: LucideIcon
  // Where the switcher navigates when this studio is picked (its landing tab).
  to: string
  flag: FeatureFlagKey
  /** VIEW permission gate for the whole studio nav entry. */
  requiresPermission?: PermissionKey
  tabs: StudioTab[]
}

export const STUDIOS: Studio[] = [
  {
    id: "master-data",
    labelKey: "MC_TAB_MASTER_DATA",
    icon: Database,
    to: `${MC_BASE}/master-data/suppliers`,
    flag: "MC_MASTER_DATA",
    requiresPermission: PERMISSIONS.MC_MASTER_DATA_VIEW,
    tabs: [
      {
        labelKey: "MC_MD_TAB_SUPPLIERS",
        icon: Building2,
        to: `${MC_BASE}/master-data/suppliers`,
        flag: "MC_MASTER_DATA_SUPPLIERS",
        requiresPermission: PERMISSIONS.MC_MASTER_DATA_VIEW,
      },
      {
        labelKey: "MC_MD_TAB_ARTICLES",
        icon: Package,
        to: `${MC_BASE}/master-data/articles`,
        flag: "MC_MASTER_DATA_ARTICLES",
        requiresPermission: PERMISSIONS.MC_MASTER_DATA_VIEW,
      },
      {
        labelKey: "MC_MD_TAB_SUBSTANCES",
        icon: FlaskConical,
        to: `${MC_BASE}/master-data/substances`,
        flag: "MC_MASTER_DATA_SUBSTANCES",
        requiresPermission: PERMISSIONS.MC_MASTER_DATA_VIEW,
      },
      {
        labelKey: "MC_MD_TAB_PRODUCTS",
        icon: Boxes,
        to: `${MC_BASE}/master-data/products`,
        flag: "MC_MASTER_DATA_PRODUCTS",
        requiresPermission: PERMISSIONS.MC_MASTER_DATA_VIEW,
      },
      {
        labelKey: "MC_MD_TAB_CUSTOMERS",
        icon: Users,
        to: `${MC_BASE}/master-data/customers`,
        flag: "MC_MASTER_DATA_CUSTOMERS",
        requiresPermission: PERMISSIONS.MC_MASTER_DATA_VIEW,
      },
    ],
  },
  {
    id: "customer-studio",
    labelKey: "MC_TAB_CUSTOMER_STUDIO",
    icon: Inbox,
    to: `${MC_BASE}/customer-studio/dashboard`,
    flag: "MC_CUSTOMER_STUDIO",
    // Show the studio if the member can view questionnaires OR conformity.
    // Sub-tabs then filter independently, so a member with only conformity
    // access lands on that tab (via firstEnabledTabPath).
    requiresPermission: PERMISSIONS.MC_QUESTIONNAIRES_VIEW,
    tabs: [
      {
        labelKey: "MC_CS_TAB_DASHBOARD",
        icon: LayoutDashboard,
        to: `${MC_BASE}/customer-studio/dashboard`,
        flag: "MC_CUSTOMER_STUDIO_DASHBOARD",
        requiresPermission: PERMISSIONS.MC_QUESTIONNAIRES_VIEW,
        end: true,
      },
      {
        labelKey: "MC_CS_TAB_CONFORMITY",
        icon: FileCheck2,
        to: `${MC_BASE}/customer-studio/conformity`,
        flag: "MC_CUSTOMER_STUDIO_CONFORMITY",
        requiresPermission: PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW,
      },
      {
        labelKey: "MC_CS_TAB_REQUESTS",
        icon: Inbox,
        to: `${MC_BASE}/customer-studio/requests`,
        flag: "MC_CUSTOMER_STUDIO_REQUESTS",
        requiresPermission: PERMISSIONS.MC_QUESTIONNAIRES_VIEW,
        end: true,
      },
    ],
  },
  {
    id: "supplier-studio",
    labelKey: "MC_TAB_SUPPLIER_STUDIO",
    icon: Factory,
    to: `${MC_BASE}/supplier-studio/dashboard`,
    flag: "MC_SUPPLIER_STUDIO",
    requiresPermission: PERMISSIONS.MC_DECLARATIONS_VIEW,
    tabs: [
      {
        labelKey: "MC_SD_TAB_DASHBOARD",
        icon: LayoutDashboard,
        to: `${MC_BASE}/supplier-studio/dashboard`,
        flag: "MC_SUPPLIER_STUDIO_DASHBOARD",
        requiresPermission: PERMISSIONS.MC_DECLARATIONS_VIEW,
      },
      {
        labelKey: "MC_SD_TAB_DECLARATIONS",
        icon: FileText,
        to: `${MC_BASE}/supplier-studio/declarations`,
        flag: "MC_SUPPLIER_STUDIO_DECLARATIONS",
        requiresPermission: PERMISSIONS.MC_DECLARATIONS_VIEW,
      },
      {
        labelKey: "MC_SD_TAB_INQUIRY",
        icon: Send,
        to: `${MC_BASE}/supplier-studio/inquiry`,
        flag: "MC_SUPPLIER_STUDIO_INQUIRY",
        requiresPermission: PERMISSIONS.MC_INQUIRIES_VIEW,
      },
    ],
  },
  {
    id: "company-index",
    labelKey: "CI_TITLE",
    icon: Building2,
    to: `${MC_BASE}/company-index`,
    flag: "COMPANY_INDEX",
    requiresPermission: PERMISSIONS.MC_DECLARATIONS_VIEW,
    tabs: [],
  },
]

// SIDEBAR_V2: master-data swaps to a Layers glyph (Database is used elsewhere
// in the app for a different meaning); the other two studios keep their icon.
export const STUDIO_ICONS_V2: Record<string, LucideIcon> = {
  "master-data": Layers,
  "customer-studio": Inbox,
  "supplier-studio": Factory,
}

// Where a studio click should land: its first enabled tab (flag on AND
// permission granted), or its own fallback `to` if every tab is filtered out.
export const firstEnabledTabPath = (
  studio: Studio,
  flags: FeatureFlagMap,
  hasPermission: (key: string) => boolean = () => true,
): string =>
  studio.tabs.filter(
    (t) =>
      flags[t.flag] && (!t.requiresPermission || hasPermission(t.requiresPermission)),
  )[0]?.to ?? studio.to
